import { z } from 'zod';
import { EFormBuilderFieldType } from '@/types';

/**
 * Shared validation for filled-in form builder fields, used by both the
 * Documents and Visit Notes entry forms.
 */

const baseFieldSchema = z.object({
  id: z.string(),
});

const optionSchema = z.object({
  id: z.string(),
  value: z.string(),
});

export const uploadDocumentSchema =
  /**
   * An uploaded document, as referenced from an answer.
   */
  z.object({
    id: z.string(),
    name: z.string(),
    uri: z.string(),
  });

const textFieldSchema = baseFieldSchema.extend({
  type: z.literal(EFormBuilderFieldType.TEXT),
  text: z.string(),
});

const checkboxFieldSchema = baseFieldSchema.extend({
  type: z.literal(EFormBuilderFieldType.CHECKBOX),
  label: z.string(),
  options: z.array(optionSchema),
  required: z.boolean().optional(),
  answer: z.array(z.string()).nullable().optional(),
});

const singleLineInputFieldSchema = baseFieldSchema.extend({
  type: z.literal(EFormBuilderFieldType.SINGLE_LINE_INPUT),
  label: z.string(),
  placeholder: z.string(),
  required: z.boolean().optional(),
  answer: z.string().nullable().optional(),
});

const multilineInputFieldSchema = baseFieldSchema.extend({
  type: z.literal(EFormBuilderFieldType.MULTILINE_INPUT),
  label: z.string(),
  placeholder: z.string(),
  required: z.boolean().optional(),
  answer: z.string().nullable().optional(),
});

const singleChoiceFieldSchema = baseFieldSchema.extend({
  type: z.literal(EFormBuilderFieldType.SINGLE_CHOICE),
  label: z.string(),
  options: z.array(optionSchema),
  required: z.boolean().optional(),
  answer: z.array(z.string()).nullable().optional(),
});

const multipleChoiceFieldSchema = baseFieldSchema.extend({
  type: z.literal(EFormBuilderFieldType.MULTIPLE_CHOICE),
  label: z.string(),
  options: z.array(optionSchema),
  required: z.boolean().optional(),
  answer: z.array(z.string()).nullable().optional(),
});

const dropdownFieldSchema = baseFieldSchema.extend({
  type: z.literal(EFormBuilderFieldType.DROPDOWN),
  label: z.string(),
  placeholder: z.string(),
  options: z.array(optionSchema),
  required: z.boolean().optional(),
  answer: z.array(z.string()).nullable().optional(),
});

const dateFieldSchema = baseFieldSchema.extend({
  type: z.literal(EFormBuilderFieldType.DATE),
  label: z.string(),
  required: z.boolean().optional(),
  answer: z.string().nullable().optional(),
});

const fileFieldSchema = baseFieldSchema.extend({
  type: z.literal(EFormBuilderFieldType.FILE),
  label: z.string(),
  required: z.boolean().optional(),
  answer: z.array(uploadDocumentSchema).nullable().optional(),
});

export const entryFieldUnion =
  /**
   * A single filled-in field, discriminated by `type`. Answers are optional
   * here; each form decides which ones it requires.
   */
  z.discriminatedUnion('type', [
    textFieldSchema,
    checkboxFieldSchema,
    singleLineInputFieldSchema,
    multilineInputFieldSchema,
    singleChoiceFieldSchema,
    multipleChoiceFieldSchema,
    dropdownFieldSchema,
    dateFieldSchema,
    fileFieldSchema,
  ]);

/**
 * Reports whether the field accepts an answer. TEXT is static content, so it
 * never does.
 * @param field - The field to check.
 * @param field.type - The field's type.
 * @returns True when the field accepts an answer.
 */
export const isEntryFieldAnswerable = (field: {
  type: EFormBuilderFieldType;
}): boolean => field.type !== EFormBuilderFieldType.TEXT;

/**
 * Reports whether the field holds an answer with content. Blank strings and
 * empty or blank collections don't count.
 * @param field - The field to check.
 * @param field.type - The field's type.
 * @param field.answer - The current answer.
 * @returns True when the field has been answered.
 */
export const isEntryFieldAnswered = (field: {
  type: EFormBuilderFieldType;
  answer?: unknown;
}): boolean => {
  if (!isEntryFieldAnswerable(field)) return false;

  const { answer } = field;

  if (typeof answer === 'string') return answer.trim() !== '';
  if (Array.isArray(answer)) {
    return answer.some((entry) =>
      typeof entry === 'string' ? entry.trim() !== '' : Boolean(entry),
    );
  }

  return false;
};

/**
 * Flags a required field left unanswered, reporting at `answer`.
 * @param field - The field being refined.
 * @param field.type - The field's type.
 * @param field.required - Whether an answer is mandatory.
 * @param field.answer - The current answer.
 * @param ctx - The zod refinement context.
 */
export const validateRequiredEntryField = (
  field: {
    type: EFormBuilderFieldType;
    required?: boolean;
    answer?: unknown;
  },
  ctx: z.RefinementCtx,
) => {
  if (!isEntryFieldAnswerable(field)) return;
  if (!field.required) return;
  if (isEntryFieldAnswered(field)) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: 'zod.this-field-is-required',
    path: ['answer'],
  });
};
